/**
 * Encodage polyline précision 6.
 *
 * Format utilisé par OSRM (`geometries=polyline6`) et par les géométries de
 * way in/out embarquées dans les fichiers GPX exportés.
 *
 * Portage de `OsrmRouter.decodePolyline6` / `encodePolyline6`.
 */

import { LIMITS, isValidLat, isValidLon } from './types'

/** Facteur de précision : six décimales, soit environ 10 cm. */
const PRECISION = 1e6

export interface PolylinePoint {
  readonly lat: number
  readonly lon: number
}

/**
 * Décode une polyline en liste de points.
 *
 * Une chaîne tronquée n'est pas une erreur : on renvoie les points décodés
 * jusqu'au point de coupure. C'est le comportement de l'application Android,
 * et il permet d'exploiter un fichier partiellement corrompu.
 */
export function decodePolyline6(
  encoded: string,
  maxPoints: number = LIMITS.MAX_POINTS_PER_TRACK,
): PolylinePoint[] {
  const points: PolylinePoint[] = []
  const length = encoded.length

  let index = 0
  let lat = 0
  let lon = 0

  while (index < length && points.length < maxPoints) {
    let shift = 0
    let result = 0
    let byte = 0

    // Latitude
    do {
      if (index >= length) return points
      byte = encoded.charCodeAt(index++) - 63
      result |= (byte & 0x1f) << shift
      shift += 5
    } while (byte >= 0x20)

    lat = (lat + (result & 1 ? ~(result >> 1) : result >> 1)) | 0

    shift = 0
    result = 0

    // Longitude
    do {
      if (index >= length) return points
      byte = encoded.charCodeAt(index++) - 63
      result |= (byte & 0x1f) << shift
      shift += 5
    } while (byte >= 0x20)

    lon = (lon + (result & 1 ? ~(result >> 1) : result >> 1)) | 0

    points.push({ lat: lat / PRECISION, lon: lon / PRECISION })
  }

  return points
}

/**
 * Encode une liste de points.
 *
 * Les caractères produits sont tous dans la plage ASCII 63-126, donc sans
 * `&`, `<` ni `>` : le résultat s'insère tel quel dans un document XML.
 */
export function encodePolyline6(points: readonly PolylinePoint[]): string {
  const parts: string[] = []

  let prevLat = 0
  let prevLon = 0

  for (const point of points) {
    const lat = Math.round(point.lat * PRECISION) | 0
    const lon = Math.round(point.lon * PRECISION) | 0

    parts.push(encodeSignedNumber(lat - prevLat))
    parts.push(encodeSignedNumber(lon - prevLon))

    prevLat = lat
    prevLon = lon
  }

  return parts.join('')
}

function encodeSignedNumber(value: number): string {
  // Décalage en zigzag : le bit de signe passe en poids faible, ce qui rend
  // les petits deltas négatifs aussi courts que les positifs.
  let v = value << 1
  if (value < 0) {
    v = ~v
  }

  let out = ''
  // Décalage non signé : un delta négatif deviendrait une boucle infinie
  // avec un décalage arithmétique.
  while (v >= 0x20) {
    out += String.fromCharCode((0x20 | (v & 0x1f)) + 63)
    v >>>= 5
  }
  out += String.fromCharCode(v + 63)

  return out
}

/**
 * Décode une polyline et refuse le résultat s'il n'est pas exploitable.
 *
 * Une géométrie de moins de deux points, ou comportant une coordonnée hors
 * bornes, est rejetée : mieux vaut recalculer l'itinéraire que d'afficher
 * un tracé aberrant.
 */
export function decodePolyline6Validated(
  encoded: string,
  maxPoints?: number,
): PolylinePoint[] | null {
  if (encoded.length === 0 || encoded.length > LIMITS.MAX_WAY_ROUTE_CHARS) {
    return null
  }

  const points = decodePolyline6(encoded, maxPoints)
  if (points.length < 2) {
    return null
  }

  for (const point of points) {
    if (!isValidLat(point.lat) || !isValidLon(point.lon)) {
      return null
    }
  }

  return points
}
